import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { app, media } from 'context';
import { Layout, Text } from 'ds';

import { TESTIMONIALS } from './homeV10Constants';
import Reveal from './Reveal';

const AVATAR_GRADIENTS = [
  'linear-gradient(135deg,#f97316,#fb923c)',
  'linear-gradient(135deg,#0ea5e9,#6366f1)',
  'linear-gradient(135deg,#16a34a,#059669)',
  'linear-gradient(135deg,#e11d48,#f43f5e)',
  'linear-gradient(135deg,#0891b2,#06b6d4)',
  'linear-gradient(135deg,#d97706,#f59e0b)',
  'linear-gradient(135deg,#65a30d,#84cc16)'
] as const;

const HOLD_MS = 4000;
const TRANSITION_MS = 480;

const QUOTE_TEXT_STYLE = {
  textAlign: 'center' as const,
  fontSize: 'clamp(22px, 3.5vw, 44px)',
  fontWeight: 700,
  letterSpacing: '-0.025em',
  lineHeight: 1.25,
  margin: 0
};

interface TestimonialSlideContentProps {
  slideIndex: number;
}

const TestimonialSlideContent: React.FC<TestimonialSlideContentProps> = ({ slideIndex }) => {
  const t = TESTIMONIALS[slideIndex];
  return (
    <>
      <Text size="headline-lg" color="white" __style={QUOTE_TEXT_STYLE}>
        &ldquo;{t.quote}&rdquo;
      </Text>
      <Layout flex align="center" gap={12} marginTop={20}>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: AVATAR_GRADIENTS[slideIndex],
            display: 'grid',
            placeItems: 'center',
            fontWeight: 700,
            color: '#ffffff',
            fontSize: 16
          }}
        >
          {t.initial}
        </div>
        <Text size="body-md" color="gray-400" __style={{ color: 'rgba(255,255,255,0.5)' }}>
          {t.by}
        </Text>
      </Layout>
    </>
  );
};

const CustomersTestimonialsSection: React.FC = () => {
  const { isMobile } = useContext(media);
  const { contentPaddingX } = useContext(app);
  const [activeIdx, setActiveIdx] = useState(0);
  const [leavingIdx, setLeavingIdx] = useState<number | null>(null);
  const isTransitioningRef = useRef(false);
  const clearTimerRef = useRef<number | null>(null);

  const endTransition = useCallback(() => {
    setLeavingIdx(null);
    isTransitioningRef.current = false;
  }, []);

  useEffect(() => {
    if (leavingIdx === null) return;
    if (clearTimerRef.current !== null) {
      window.clearTimeout(clearTimerRef.current);
    }
    clearTimerRef.current = window.setTimeout(() => {
      endTransition();
      clearTimerRef.current = null;
    }, TRANSITION_MS);
    return () => {
      if (clearTimerRef.current !== null) {
        window.clearTimeout(clearTimerRef.current);
        clearTimerRef.current = null;
      }
    };
  }, [leavingIdx, endTransition]);

  const goTo = useCallback((nextIdx: number) => {
    if (isTransitioningRef.current) return;
    setActiveIdx(prev => {
      if (nextIdx === prev) return prev;
      isTransitioningRef.current = true;
      setLeavingIdx(prev);
      return nextIdx;
    });
  }, []);

  const advance = useCallback(() => {
    if (isTransitioningRef.current) return;
    setActiveIdx(prev => {
      isTransitioningRef.current = true;
      setLeavingIdx(prev);
      return (prev + 1) % TESTIMONIALS.length;
    });
  }, []);

  useEffect(() => {
    const id = window.setInterval(advance, HOLD_MS);
    return () => window.clearInterval(id);
  }, [advance]);

  const entering = leavingIdx !== null;

  return (
    <div className="HomeV10-customersSection">
      <Layout paddingY={isMobile ? 72 : 112} paddingX={contentPaddingX} position="relative">
        <Layout justify="center" flex>
          <Layout maxWidth={1280} flexGrow={1} align="center" direction="column">
            <Reveal>
              <Text
                size="body-lg"
                semibold
                color="gray-400"
                __style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  marginBottom: 0,
                  color: 'rgba(255,255,255,0.5)'
                }}
              >
                Teams that flipped the switch
              </Text>
            </Reveal>
            <Reveal delayStep={1}>
              <Layout
                marginTop={48}
                marginBottom={56}
                __style={{
                  minHeight: 200,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <div className="HomeV10-testimonialStage">
                  {leavingIdx !== null ? (
                    <div
                      key={`leave-${leavingIdx}`}
                      className="HomeV10-testimonialCard HomeV10-testimonialCard--leaving"
                      aria-hidden
                    >
                      <TestimonialSlideContent slideIndex={leavingIdx} />
                    </div>
                  ) : null}
                  <div
                    key={`active-${activeIdx}`}
                    className={`HomeV10-testimonialCard${entering ? ' HomeV10-testimonialCard--entering' : ''}`}
                  >
                    <TestimonialSlideContent slideIndex={activeIdx} />
                  </div>
                </div>
                <Layout flex gap={8} marginTop={16}>
                  {TESTIMONIALS.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      className={`HomeV10-testimonialDot${i === activeIdx ? ' is-active' : ''}`}
                      aria-label={`Testimonial ${i + 1}`}
                      onClick={() => goTo(i)}
                    />
                  ))}
                </Layout>
              </Layout>
            </Reveal>
          </Layout>
        </Layout>
      </Layout>
    </div>
  );
};

export default CustomersTestimonialsSection;
